import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { CircularProgress } from "@mui/material";
import type { TaskFile } from "../types/types";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";
import {
  buildFileDownloadUrl,
  buildFilePreviewUrl,
  getFilePreviewKind,
} from "../utils/fileApi.ts";

interface FilePreviewerProps {
  file: TaskFile;
  honbuId: number;
  color: PaletteColor;
  onClose: () => void;
}

/**
 * 添付ファイルの全画面プレビュー（F017）。
 * 判定はファイル名の拡張子で行い、画像 → 動画 → PDF / Office(iframe) → 非対応の順に分岐する。
 * Office は Microsoft Office Online Viewer に委譲するため、サーバーが外部到達可能であることが前提。
 */
export function FilePreviewer({ file, honbuId, color, onClose }: FilePreviewerProps) {
  const kind = getFilePreviewKind(file.fileName);
  const previewUrl = buildFilePreviewUrl(file.fileId, honbuId);
  const downloadUrl = buildFileDownloadUrl(file.fileId, honbuId);
  const accent = PaletteColorHex[color] ?? PaletteColorHex.Blue;

  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);

  // ファイル切替時に読み込み状態とズームをリセットする
  useEffect(() => {
    setImageLoaded(false);
    setZoom(1);
  }, [file.fileId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const officeViewerUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
    `${window.location.origin}${downloadUrl}`,
  )}`;

  const renderBody = () => {
    switch (kind) {
      case "image":
        return (
          <div className="w-full h-full overflow-auto flex items-center justify-center">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <CircularProgress sx={{ color: "#fff" }} />
              </div>
            )}
            <img
              src={previewUrl}
              alt={file.fileName}
              onLoad={() => setImageLoaded(true)}
              className="transition-opacity duration-300"
              style={{
                opacity: imageLoaded ? 1 : 0,
                transform: `scale(${zoom})`,
                transformOrigin: "center center",
                maxWidth: zoom === 1 ? "100%" : "none",
                maxHeight: zoom === 1 ? "100%" : "none",
                objectFit: "contain",
              }}
            />
          </div>
        );
      case "video":
        return (
          <video
            key={file.fileId}
            src={previewUrl}
            controls
            className="max-w-full max-h-full"
          />
        );
      case "pdf":
        return (
          <iframe
            title={file.fileName}
            src={previewUrl}
            className="w-full h-full border-0 bg-white"
          />
        );
      case "office":
        return (
          <iframe
            title={file.fileName}
            src={officeViewerUrl}
            className="w-full h-full border-0 bg-white"
          />
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center text-gray-200 gap-4">
            <InsertDriveFileIcon sx={{ fontSize: 64 }} />
            <p>プレビュー非対応のファイル形式です</p>
            <a
              href={downloadUrl}
              className="px-4 py-2 rounded text-white"
              style={{ backgroundColor: accent }}
            >
              ダウンロード
            </a>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-[1400] flex flex-col">
      {/* ヘッダー */}
      <div
        className="flex items-center justify-between px-3 py-2 shrink-0"
        style={{
          background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/20 text-white shrink-0"
            aria-label="閉じる"
          >
            <CloseIcon />
          </button>
          <span className="text-white font-medium truncate" title={file.fileName}>
            {file.fileName}
          </span>
        </div>
        <a
          href={downloadUrl}
          className="flex items-center gap-1 px-3 py-1.5 rounded bg-white/20 hover:bg-white/30 text-white text-sm shrink-0"
        >
          <DownloadIcon fontSize="small" />
          <span className="hidden sm:inline">ダウンロード</span>
        </a>
      </div>

      {/* 本体（PDF ビューア風のグレー背景） */}
      <div
        className="relative flex-1 flex items-center justify-center overflow-hidden"
        style={{ backgroundColor: "#525659" }}
      >
        {renderBody()}

        {/* 画像のみ: ズームコントロール */}
        {kind === "image" && imageLoaded && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/60 rounded-full px-2 py-1">
            <button
              onClick={() => setZoom((z) => Math.max(0.2, Math.round((z - 0.25) * 100) / 100))}
              className="p-1.5 rounded-full text-white hover:bg-white/20"
              aria-label="縮小"
            >
              <RemoveIcon fontSize="small" />
            </button>
            <span className="text-white text-xs w-12 text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom((z) => Math.min(8, Math.round((z + 0.25) * 100) / 100))}
              className="p-1.5 rounded-full text-white hover:bg-white/20"
              aria-label="拡大"
            >
              <AddIcon fontSize="small" />
            </button>
            <button
              onClick={() => setZoom(1)}
              className="p-1.5 rounded-full text-white hover:bg-white/20"
              aria-label="リセット"
            >
              <RestartAltIcon fontSize="small" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
